import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Flower2, Heart } from "lucide-react";
import { api, formatPrice, resolveImage } from "@/lib/api";

// Pages "boutique" où le bandeau ne s'affiche pas
const HIDDEN_PREFIXES = ["/boutique", "/produit", "/paiement", "/admin"];
const isHidden = (path) => HIDDEN_PREFIXES.some((p) => path.startsWith(p));

const pickRandom = (arr, n) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
};

export default function DiscoverStrip() {
  const { pathname } = useLocation();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) =>
        setProducts((data || []).filter((p) => !p.bookable && (p.stock ?? 1) > 0))
      )
      .catch(() => setProducts([]));
  }, []);

  // Nouvelle sélection à chaque changement de page :
  // 1 « Coup de cœur » (produit featured) + 3 autres produits, au hasard.
  const selection = useMemo(() => {
    const featured = products.filter((p) => p.featured);
    const others = products.filter((p) => !p.featured);
    const star = pickRandom(featured, 1);
    const rest = pickRandom(others, 4 - star.length);
    const combined = [...star, ...rest];
    // Complément si trop peu de produits non-featured disponibles
    if (combined.length < 4) {
      const remaining = pickRandom(
        products.filter((p) => !combined.some((c) => c.product_id === p.product_id)),
        4 - combined.length
      );
      combined.push(...remaining);
    }
    return combined;
  }, [products, pathname]);

  if (isHidden(pathname) || selection.length === 0) return null;

  return (
    <div data-testid="discover-strip" className="border-b border-amber-900/10 bg-sand/50">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2.5 sm:px-8">
        <div className="hidden shrink-0 items-center gap-2 text-amber-deep sm:flex">
          <Flower2 size={16} />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-bark-soft">À butiner aussi</span>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={pathname}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="flex flex-1 items-center gap-2 overflow-x-auto sm:gap-3"
          >
            {selection.map((p) => (
              <Link
                key={p.product_id}
                to={`/produit/${p.product_id}`}
                data-testid={`discover-item-${p.product_id}`}
                title={p.name}
                className="group flex min-w-[150px] shrink-0 items-center gap-2.5 rounded-full border border-amber-800/10 bg-white/70 py-1 pl-1 pr-3 transition-all duration-200 hover:border-amber-deep/40 hover:bg-white hover:shadow-sm sm:min-w-[170px]"
              >
                <img
                  src={resolveImage(p.image_url)}
                  alt={p.name}
                  loading="lazy"
                  className="h-9 w-9 shrink-0 rounded-full bg-white object-contain transition-transform duration-300 group-hover:scale-105"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1">
                    {p.featured && <Heart size={11} className="shrink-0 fill-terra text-terra" />}
                    <span className="block truncate text-xs font-medium text-bark">{p.name}</span>
                  </span>
                  <span className="block text-xs font-semibold text-amber-deep">{formatPrice(p.price_cents)}</span>
                </span>
              </Link>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
